import { _ } from "libs";

import React, { FC, useState } from "react";
import { Form, Field } from "react-final-form";
import { FORM_ERROR } from "final-form";
import { changePassword } from "../actions";
import Loader from "./loader";
import Loadable from "react-loadable";
import PasswordInput from "./forms/controls/passwordInput";

type Values = {
	currentPassword: string;
	newPassword: string;
	newPasswordConfirm: string;
};

const validate = (needCurrentPassword: boolean) => (values: Values): Values => {
	const errors = {} as Values;
	if (needCurrentPassword) {
		if (!values.currentPassword) {
			errors.currentPassword = _("validation.mustBeFilled");
		}
	}

	if (!values.newPassword) {
		errors.newPassword = _("validation.mustBeFilled");
	}
	if (values.newPasswordConfirm !== values.newPassword) {
		errors.newPasswordConfirm = _("validation.mustMatchNewPassword");
	}

	return errors;
};

type Props = {
	needCurrentPassword?: boolean;
	callback?: (res: any) => void;
};

const PasswordInputWithStrength = Loadable({
	loader: () => import("./forms/controls/passwordInputWithStrength"),
	loading: Loader,
});

const PasswordChangeForm: FC<Props> = ({ needCurrentPassword = true, callback = null }: Props) => {
	const [passwordChanged, setPasswordChanged] = useState(false);

	const onSubmit = async ({ currentPassword, newPassword, newPasswordConfirm }) => {
		const res = await changePassword(currentPassword, newPassword, newPasswordConfirm);

		if (res.error) {
			return { [FORM_ERROR]: res.error.response.data.error_description };
		}

		setPasswordChanged(true);

		if (callback) {
			callback(res);
		}
	};

	return (
		<div className="form">
			{passwordChanged ? _("password.changedSuccessfully") : null}
			<Form
				onSubmit={onSubmit}
				validate={validate(needCurrentPassword)}
				render={({ submitError, handleSubmit, submitting, pristine, invalid, values }) => (
					<form onSubmit={handleSubmit} className="changePassword lite">
						{submitError && <div className="alert error">{submitError}</div>}
						{needCurrentPassword ? (
							<Field name="currentPassword">
								{({ input, meta }) => (
									<div className="field">
										<label htmlFor="currentPassword">
											{_("passwordChangeForm.currentPassword.label")}
										</label>
										<PasswordInput
											{...input}
											id="currentPassword"
											autoComplete="current-password"
										/>
										{meta.touched && meta.error && <p className="error">{meta.error}</p>}
									</div>
								)}
							</Field>
						) : null}
						<Field name="newPassword">
							{({ input, meta }) => (
								<div className="field">
									<label htmlFor="newPassword">{_("passwordChangeForm.newPassword.label")}</label>
									<PasswordInputWithStrength {...input} minLength={8} id="newPassword" />
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
								</div>
							)}
						</Field>
						<Field name="newPasswordConfirm">
							{({ input, meta }) => (
								<div className="field">
									<label htmlFor="newPasswordConfirm">
										{_("passwordChangeForm.newPasswordConfirm.label")}
									</label>
									<PasswordInput {...input} id="newPasswordConfirm" />
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
								</div>
							)}
						</Field>
						<div className="buttons">
							<button className="button submit fullWidth" type="submit" disabled={submitting || pristine}>
								{_("passwordChangeForm.submitBtn")}
							</button>
						</div>
					</form>
				)}
			/>
		</div>
	);
};

export default PasswordChangeForm;
